import { escapeHtml, sheetNames } from "./recipesLogic";

/**
 * Tabele za štampu i Excel. Isti brojevi kao na ekranu (izmijenjene porcije, izabrane smjene, filteri):
 * štampa dobija tekst po pravilima jezika, Excel brojeve (da se mogu sabirati) i jedinicu u svojoj koloni.
 *
 * ctx: {
 *   excel: boolean, shifts: number[], price: boolean, currency: string,
 *   t: (key, params) => string, plural: (key, count) => string, name: (text) => string,
 *   qty: (value) => string, money: (value) => string
 * }
 */
const cents = (value) => Math.round((Number(value) || 0) * 100) / 100;

const costHeader = (ctx) => (ctx.currency ? `${ctx.t("recipes_shell.cost")} (${ctx.currency})` : ctx.t("recipes_shell.cost"));
const priceHeader = (ctx) =>
  ctx.currency ? `${ctx.t("recipes_shell.unit_price")} (${ctx.currency})` : ctx.t("recipes_shell.unit_price");

const shiftHeader = (ctx, shift, portions) => {
  const label = ctx.t("report_mobile.shift_short", { n: shift });
  return portions == null ? label : `${label} (${portions})`;
};

const tableHeaders = (ctx, portionsByShift) => {
  const headers = [{ text: ctx.t("grocery"), value: "name", align: "start" }];
  ctx.shifts.forEach((shift, i) => {
    headers.push({ text: shiftHeader(ctx, shift, portionsByShift ? portionsByShift[i] : null), value: `s${shift}`, align: "end" });
  });
  headers.push({ text: ctx.t("total"), value: "total", align: "end" });
  if (ctx.excel) {
    headers.push({ text: ctx.t("recipes_shell.unit"), value: "unit", align: "start" });
  }
  if (ctx.price) {
    headers.push({ text: priceHeader(ctx), value: "price", align: "end" });
    headers.push({ text: costHeader(ctx), value: "cost", align: "end" });
  }
  return headers;
};

/** Jedan red namirnice: količine po smjenama, ukupno (sa jedinicom u štampi), cijena i trošak. */
const ingredientRow = (ctx, source) => {
  const row = { name: ctx.name(source.name) };
  ctx.shifts.forEach((shift, i) => {
    const value = source.shown[i];
    row[`s${shift}`] = value ? (ctx.excel ? value : ctx.qty(value)) : "";
  });
  if (ctx.excel) {
    row.total = source.total;
    row.unit = source.measure;
  } else {
    row.total = `${ctx.qty(source.total)} ${source.measure}`.trim();
  }
  if (ctx.price) {
    row.price = source.price == null ? "" : ctx.excel ? cents(source.price) : ctx.money(source.price);
    row.cost = source.cost == null ? "" : ctx.excel ? cents(source.cost) : ctx.money(source.cost);
  }
  return row;
};

const summaryRow = (ctx, label, value) => {
  const row = { name: label };
  if (ctx.price) {
    row.cost = ctx.excel ? cents(value) : ctx.money(value);
  }
  return row;
};

/** Tabela jednog jela: entry = { c, rows } iz kartica na ekranu. */
export const cardTable = (ctx, entry) => {
  const { c, rows } = entry;
  const items = rows.map((row) => ingredientRow(ctx, row));
  if (ctx.price && rows.length) {
    items.push(summaryRow(ctx, ctx.t("recipes_shell.meal_cost"), c.cost));
    items.push(summaryRow(ctx, ctx.t("recipes_shell.per_portion"), c.perPortion));
  }
  const title = ctx.name(c.name);
  return {
    title,
    subtitle: ctx.plural("recipes_shell.portions_n", c.portions),
    headers: tableHeaders(
      ctx,
      c.cols.map((col) => ctx.qty(col.count))
    ),
    items,
    edited: c.edited,
  };
};

/** Zbirna tabela namirnica svih prikazanih jela. */
export const totalTable = (ctx, rows, edited = 0) => {
  const items = rows.map((row) => ingredientRow(ctx, row));
  if (ctx.price && rows.length) {
    items.push(summaryRow(ctx, ctx.t("recipes_shell.total_cost"), rows.reduce((total, row) => total + (row.cost || 0), 0)));
  }
  return {
    title: ctx.t("total"),
    subtitle: "",
    headers: tableHeaders(ctx, null),
    items,
    edited,
  };
};

/** Tabele za izvoz: po jelu kad je otvoren "Po jelima" (pa zbirna), a samo zbirna kad je otvoreno "Zbirno". */
export const buildTables = (ctx, { tab, cards, totals, editCount = 0 }) => {
  const tables = [];
  if (tab === "meals") {
    cards.forEach((entry) => tables.push(cardTable(ctx, entry)));
  }
  if (totals.length) {
    tables.push(totalTable(ctx, totals, editCount));
  }
  return tables;
};

/** Za štampu: naslovi i tekst u tabelama idu u HTML, pa se znakovi < > & " ' mijenjaju. */
export const printTables = (tables) =>
  tables.map((table) => ({
    ...table,
    title: escapeHtml(table.title),
    caption: "",
    headers: table.headers.map((header) => ({ ...header, text: escapeHtml(header.text) })),
    items: table.items.map((item) => Object.fromEntries(Object.entries(item).map(([key, value]) => [key, escapeHtml(value)]))),
    beforeTableHtml: table.subtitle ? `<div style="margin:2px 0 6px;font-size:13px;color:#555;text-align:center;">${escapeHtml(table.subtitle)}</div>` : "",
  }));

/** Listovi za Excel: nazivi bez zabranjenih znakova i jedinstveni. */
export const excelSheets = (tables, { subtitle, editedNote = "" }) => {
  const names = sheetNames(tables.map((table) => table.title));
  return tables.map((table, i) => ({
    name: names[i],
    title: table.title,
    subtitle: [subtitle, table.subtitle].filter(Boolean).join(" · "),
    beforeTableHtml: table.edited ? editedNote : "",
    headers: table.headers,
    items: table.items,
  }));
};

export const exportFileName = (from, to) => `recepti_${from}${to && to !== from ? `_${to}` : ""}.xlsx`;
